import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import { SyntheticEvent, useState, useEffect, useRef, useCallback, useMemo, forwardRef } from 'react';

// Wrapper to filter MUI Tab-specific props from DOM element
const TabDiv = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement> & Record<string, unknown>>((props, ref) => {
  const { fullWidth, indicator, selectionFollowsFocus, textColor, ...rest } = props;
  // Silence unused var warnings
  void fullWidth;
  void indicator;
  void selectionFollowsFocus;
  void textColor;
  return <div ref={ref} {...rest} />;
});
import ListView from './ListView';
import CardView from './CardView';
import { CustomTabContent } from './CustomTabContent';
import { TabItem } from './types/tab';
import { FavoritesProvider } from '../context/FavoritesContext';
import { IFigureCollection } from '../models/IFigureCollection';
import { IFigureDto } from '../models/IFigureDto';
import FigurService from '../services/figureService';
import FigureDetail from './FigureDetail';
import SearchField, { SearchFieldRef } from './SearchField';
import SeriesFilter from './SeriesFilter';
import TagsFilter from './TagsFilter';
//import Switch from '@mui/material/Switch';
//import FormControlLabel from '@mui/material/FormControlLabel';
import { useQueryClient } from '@tanstack/react-query';
import { useConfig } from '../config/env';
import { getCollectionColors } from '../utils/collectionColors';
import { useFigureSearchState } from '../hooks/useFigureSearchState';
import { useAppEvent } from '../hooks/useAppEvent';
import { emitAppEvent } from '../events/appEvents';

const MAX_TABS = 99; // TODO: read this value from env/appsettings

const CustomTab = (props: { customerId: number }) => {
  const { customerId } = props;
  const queryClient = useQueryClient();
  const [value, setValue] = useState('0');
  const [view, setView] = useState<'list' | 'grid'>('grid');
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collections, setCollections] = useState<IFigureCollection[]>([]);

  const [editingTab, setEditingTab] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTabToDelete, setSelectedTabToDelete] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchFieldRef = useRef<SearchFieldRef>(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [collectionMenuAnchor, setCollectionMenuAnchor] = useState<null | HTMLElement>(null);
  const addButtonRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);
  const [pendingSearchExit, setPendingSearchExit] = useState(false);

  const handleViewChange = (_event: React.MouseEvent<HTMLElement>, newView: 'list' | 'grid' | null) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  const handleForceGridView = useCallback(() => {
    setView('grid');
  }, []);

  const cfg = useConfig(); // get the /env values from the backend

  const [tabs, setTabs] = useState<TabItem[]>([
    {
      // TODO settings for favorite collection should come from env/appsettings
      id: '0',
      label: 'Favoritter',
      content: null,
      collectionId: Number(cfg?.favoritterCollectionId ?? 0),
    },
  ]);

  const currentCollection = useMemo(() => {
    const currentCollectionId = tabs.find((tab) => tab.id === value)?.collectionId;
    return collections.find((c) => c.id === currentCollectionId);
  }, [tabs, value, collections]);

  const isUseAiEnabled = currentCollection?.isDefault === true;

  // Determine if default collection is active
  const defaultCollectionId = Number(cfg?.defaultCollectionId ?? 0);
  const activeCollectionId = useMemo(() => tabs.find((t) => t.id === value)?.collectionId, [tabs, value]);
  const isDefaultActive = useMemo(
    () => (defaultCollectionId > 0 ? activeCollectionId === defaultCollectionId : currentCollection?.isDefault === true),
    [defaultCollectionId, activeCollectionId, currentCollection?.isDefault],
  );

  const { searchState, setSearchState, seriesId, setSeriesId, selectedTags, setSelectedTags, handleSearchResults, handleSearchModeChange } =
    useFigureSearchState({
      customerId,
      activeCollectionId,
      isDefaultActive,
    });

  useEffect(() => {
    const initializeTabs = async () => {
      setIsInitializing(true);
      try {
        const data = await FigurService.getCollectionsByCustomer(customerId);
        const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
        const defaultId = Number(cfg?.defaultCollectionId ?? 0);

        let defaultCollection = data.find((c) =>
          defaultId > 0 ? c.id === defaultId : (c as IFigureCollection & { isDefault?: boolean }).isDefault === true,
        );
        if (defaultCollection && defaultCollection.id === favoriteId) {
          defaultCollection = undefined;
        }

        const otherCollections = data.filter((c) => c.id !== favoriteId && (!defaultCollection || c.id !== defaultCollection.id));
        const orderedCollections = defaultCollection ? [defaultCollection, ...otherCollections] : otherCollections;

        const dynamicTabs = orderedCollections.map((collection, index) => ({
          id: String(index + 1),
          label: collection.name,
          content: null,
          collectionId: collection.id,
        }));
        setTabs([
          {
            id: '0',
            label: 'Favoritter',
            content: null,
            collectionId: Number(cfg?.favoritterCollectionId ?? 0),
          },
          ...dynamicTabs,
        ]);
        setCollections(data);
      } catch (err) {
        setError(`Failed to initialize tabs: ${err}`);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeTabs();
  }, [customerId, cfg?.favoritterCollectionId, cfg?.defaultCollectionId]);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const data = await FigurService.getCollectionsByCustomer(customerId);

        // Always maintain order: Favorite, Default, Others
        const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
        const defaultId = Number(cfg?.defaultCollectionId ?? 0);
        let defaultCollection = data.find((c) =>
          defaultId > 0 ? c.id === defaultId : (c as IFigureCollection & { isDefault?: boolean }).isDefault === true,
        );
        if (defaultCollection && defaultCollection.id === favoriteId) {
          defaultCollection = undefined;
        }
        const otherCollections = data.filter((c) => c.id !== favoriteId && (!defaultCollection || c.id !== defaultCollection.id));
        const orderedCollections = defaultCollection ? [defaultCollection, ...otherCollections] : otherCollections;

        // Build tabs in desired order; keep existing selected value when possible
        const dynamicTabs = orderedCollections.map((collection, index) => ({
          id: String(index + 1),
          label: collection.name,
          content: null,
          collectionId: collection.id,
        }));

        setTabs((prevTabs) => {
          // Preserve current selection label when possible
          const currentId = prevTabs.find((t) => t.id === value)?.collectionId;
          const nextTabs = [
            {
              id: '0',
              label: 'Favoritter',
              content: null,
              collectionId: favoriteId,
            },
            ...dynamicTabs,
          ];
          // Reindex ids to 0..n and keep value stable if possible
          const reindexed = nextTabs.map((t, i) => ({ ...t, id: String(i) }));
          const idx = reindexed.findIndex((t) => t.collectionId === currentId);
          if (idx >= 0) setValue(String(idx));
          return reindexed;
        });

        setCollections(data);
      } catch (err) {
        setError(`Failed to fetch collections by customer ${err}`);
      }
    };

    fetchCollections();
  }, [customerId, cfg?.favoritterCollectionId, cfg?.defaultCollectionId]);

  // When a new collection is created elsewhere (e.g., FigureDetail add field),
  // add a new tab for it and select it
  useAppEvent('collectionCreated', (detail) => {
    setCollections((prev) => [...prev, detail]);
    setTabs((prev) => {
      const newId = String(prev.length);
      const next = [...prev, { id: newId, label: detail.name, content: null, collectionId: detail.id }];
      setValue(newId);
      return next;
    });
  });

  const startEditing = (tab: TabItem) => {
    const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
    const defaultId = Number(cfg?.defaultCollectionId ?? 0);
    const isFavorite = tab.collectionId === favoriteId;
    const isDefault = defaultId > 0 && tab.collectionId === defaultId;
    if (isFavorite || isDefault) return;
    setEditingTab(tab.id);
    setEditValue(tab.label);
  };

  useEffect(() => {
    // Get initial container width
    if (containerRef.current && !containerWidth) {
      setContainerWidth(containerRef.current.getBoundingClientRect().width);
    }

    // Add resize listener
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.getBoundingClientRect().width);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [containerWidth]);

  const handleTabClick = useCallback(
    (tabId: string) => {
      const prevCollectionId = tabs.find((t) => t.id === value)?.collectionId;
      const prev = collections.find((c) => c.id === prevCollectionId);

      setValue(tabId);
      setTabs((prevTabs) => prevTabs.map((tab) => (tab.id === tabId ? { ...tab, content: null } : tab)));

      const newCollectionId = tabs.find((t) => t.id === tabId)?.collectionId;

      if (prev?.isDefault && newCollectionId !== prevCollectionId) {
        searchFieldRef.current?.clearSearch();
        setSearchState((s) => ({ ...s, isSearchMode: false, results: [], totalCount: 0, hasMore: false }));
      }
    },
    [tabs, collections, value, setTabs, setValue, setSearchState, searchFieldRef],
  );

  const handleAvatarCollectionClick = useCallback(
    (collectionId: number) => {
      const tab = tabs.find((t) => t.collectionId === collectionId);
      if (tab) handleTabClick(tab.id);
    },
    [tabs, handleTabClick],
  );

  const getFigureCollectionId = useCallback((figure: IFigureDto, fallbackCollectionId: number) => {
    if (figure.collectionId && figure.collectionId > 0) {
      return figure.collectionId;
    }
    const membershipIds = Object.keys(figure.collectionMemberships || {})
      .map((key) => Number(key))
      .filter((id) => id > 0);
    if (membershipIds.length > 0) {
      return membershipIds[0];
    }
    return fallbackCollectionId;
  }, []);

  const handleFigureSelect = useCallback(
    (figure: IFigureDto, collectionId: number) => {
      const targetTab = tabs.find((t) => t.collectionId === collectionId);
      if (targetTab && targetTab.id !== value) {
        setValue(targetTab.id);
      }

      setTabs((prev) =>
        prev.map((t) =>
          t.collectionId === collectionId
            ? {
                ...t,
                content: (
                  <FigureDetail
                    figure={figure}
                    collectionId={collectionId}
                    onForceGridView={handleForceGridView}
                    onAvatarClick={handleAvatarCollectionClick}
                    collections={collections}
                  />
                ),
              }
            : t,
        ),
      );

      if (searchState.isSearchMode) {
        setPendingSearchExit(true);
      }
    },
    [tabs, value, setValue, handleForceGridView, handleAvatarCollectionClick, collections, searchState.isSearchMode],
  );

  const createFigureSelectHandler = useCallback(
    (tabCollectionId: number) => (figure: IFigureDto) => {
      const destinationCollectionId = searchState.isSearchMode ? getFigureCollectionId(figure, tabCollectionId) : tabCollectionId;
      handleFigureSelect(figure, destinationCollectionId);
    },
    [searchState.isSearchMode, getFigureCollectionId, handleFigureSelect],
  );

  useEffect(() => {
    if (!pendingSearchExit) {
      return;
    }

    const activeTab = tabs.find((tab) => tab.id === value);
    const detailMounted = Boolean(activeTab?.content);

    if (!detailMounted) {
      return;
    }

    if (searchFieldRef.current) {
      searchFieldRef.current.clearSearch();
    }

    setSearchState((prev) => ({
      ...prev,
      isSearchMode: false,
      results: [],
      totalCount: 0,
    }));

    setPendingSearchExit(false);
  }, [pendingSearchExit, tabs, value]);

  const handleChange = (_: SyntheticEvent, newValue: string) => {
    handleTabClick(newValue);
  };

  const handleAddButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (addButtonRef.current) {
      setCollectionMenuAnchor(addButtonRef.current);
    }
  };

  const handleCollectionMenuClose = (event?: unknown | React.MouseEvent | React.KeyboardEvent, _reason?: 'backdropClick' | 'escapeKeyDown') => {
    // Don't close if clicking inside the menu content
    if (event && typeof event === 'object' && event !== null && 'target' in event && popoverContentRef.current) {
      const target = (event as { target?: unknown }).target;
      if (target && popoverContentRef.current.contains(target as Node)) {
        return;
      }
    }
    setCollectionMenuAnchor(null);
    setNewCollectionName('');
  };

  const handleAddTab = async (collectionName?: string) => {
    if (tabs.length >= MAX_TABS) {
      setError(`Maximum ${MAX_TABS} tabs allowed`);
      return;
    }

    const trimmedName = collectionName?.trim() || '';
    if (!trimmedName) {
      setError('Collection name cannot be empty');
      return;
    }

    const newTabId = String(tabs.length);

    try {
      // Ensure customerId is treated as a number
      const newCollection = await FigurService.addCollection(trimmedName, Number(customerId), '');

      // Persist an initial color derived from the new collection
      const derived = getCollectionColors({ id: String(newCollection.id), name: newCollection.name });
      const savedWithColor = await FigurService.updateCollectionDetails(
        newCollection.id,
        newCollection.name,
        Number(customerId),
        newCollection.comment ?? '',
        newCollection.headerComment ?? '',
        derived.bg,
      );

      const newTab: TabItem = {
        id: newTabId,
        label: trimmedName,
        content: null,
        collectionId: newCollection.id,
      };

      // Ensure color calculation works immediately by updating collections
      setCollections((prev) => [...prev, savedWithColor]);

      setTabs([...tabs, newTab]);
      setValue(newTabId);
      setError(null);
      setNewCollectionName('');
      handleCollectionMenuClose();
    } catch (err) {
      setError(`Failed to create new collection: ${err}`);
    }
  };

  const handleCreateCollection = async () => {
    await handleAddTab(newCollectionName);
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedTabToDelete) return;

    try {
      const tabToDelete = tabs.find((tab) => tab.id === selectedTabToDelete);
      if (!tabToDelete) {
        setError('Tab not found');
        return;
      }

      // Guard: prevent deleting favorite or default collections
      const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
      const defaultId = Number(cfg?.defaultCollectionId ?? 0);
      if (tabToDelete.collectionId === favoriteId || (defaultId > 0 && tabToDelete.collectionId === defaultId)) {
        // Do not show UI error; simply block and close dialog if open
        setIsDeleteDialogOpen(false);
        setSelectedTabToDelete(null);
        return;
      }

      // Existing deletion logic
      const response = await FigurService.getFigures(tabToDelete.collectionId);
      await Promise.all(response.data.map((figure) => FigurService.removeFigureFromCollection(tabToDelete.collectionId, figure.imageId)));
      // Notify UI for each removed figure so avatars update immediately
      response.data.forEach((figure) => {
        emitAppEvent('figureRemoved', { removedFigureId: figure.imageId, collectionId: tabToDelete.collectionId });
      });

      // Optimistically update all list/grid caches so switching views doesn't resurrect avatars
      const removedIds = new Set(response.data.map((f) => f.imageId));
      // Update grid caches (figures-grid)
      const gridQueries = queryClient.getQueriesData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>({
        queryKey: ['figures-grid'],
      });
      gridQueries.forEach(([key, oldData]) => {
        if (!oldData || !oldData.pages) return;
        const keyArr = key as unknown as (string | number | null | undefined)[];
        const keyCollectionId = typeof keyArr[1] === 'number' ? (keyArr[1] as number) : undefined;
        queryClient.setQueryData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>(key as unknown as readonly unknown[], {
          ...oldData,
          pages: oldData.pages.map((page) => {
            const originalCount = page.data.length;
            const mapped = page.data.map((f: IFigureDto) =>
              removedIds.has(f.imageId)
                ? {
                    ...f,
                    collectionMemberships: Object.fromEntries(
                      Object.entries(f.collectionMemberships).filter(([k]) => Number(k) !== tabToDelete.collectionId),
                    ),
                  }
                : f,
            );
            const filtered = keyCollectionId === tabToDelete.collectionId ? mapped.filter((f: IFigureDto) => !removedIds.has(f.imageId)) : mapped;
            const removedFromPage = originalCount - filtered.length;
            return {
              ...page,
              data: filtered,
              totalCount:
                keyCollectionId === tabToDelete.collectionId && removedFromPage > 0
                  ? Math.max(0, (page.totalCount || 0) - removedFromPage)
                  : page.totalCount,
            };
          }),
        });
      });

      // Update list caches (figures)
      const listQueries = queryClient.getQueriesData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>({ queryKey: ['figures'] });
      listQueries.forEach(([key, oldData]) => {
        if (!oldData || !oldData.pages) return;
        const keyArr = key as unknown as (string | number | null | undefined)[];
        const keyCollectionId = typeof keyArr[1] === 'number' ? (keyArr[1] as number) : undefined;
        queryClient.setQueryData<{ pages?: Array<{ data: IFigureDto[]; totalCount?: number }> }>(key as unknown as readonly unknown[], {
          ...oldData,
          pages: oldData.pages.map((page) => {
            const originalCount = page.data.length;
            const mapped = page.data.map((f: IFigureDto) =>
              removedIds.has(f.imageId)
                ? {
                    ...f,
                    collectionMemberships: Object.fromEntries(
                      Object.entries(f.collectionMemberships).filter(([k]) => Number(k) !== tabToDelete.collectionId),
                    ),
                  }
                : f,
            );
            const filtered = keyCollectionId === tabToDelete.collectionId ? mapped.filter((f: IFigureDto) => !removedIds.has(f.imageId)) : mapped;
            const removedFromPage = originalCount - filtered.length;
            return {
              ...page,
              data: filtered,
              totalCount:
                keyCollectionId === tabToDelete.collectionId && removedFromPage > 0
                  ? Math.max(0, (page.totalCount || 0) - removedFromPage)
                  : page.totalCount,
            };
          }),
        });
      });

      await FigurService.removeCollection(tabToDelete.collectionId, Number(customerId));

      const newTabs = tabs.filter((tab) => tab.id !== selectedTabToDelete);
      setTabs(newTabs.map((tab, index) => ({ ...tab, id: index.toString() })));

      // Update tab selection logic
      if (value === selectedTabToDelete) {
        setValue(Math.max(parseInt(selectedTabToDelete, 10) - 1, 0).toString());
      } else if (parseInt(value, 10) > parseInt(selectedTabToDelete, 10)) {
        setValue((parseInt(value, 10) - 1).toString());
      }

      setError(null);
      // Keep existing behavior (fire global event) but provide a safe detail payload.
      emitAppEvent('figureDataUpdated', []);
    } catch (err) {
      setError(`Failed to delete collection: ${err}`);
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedTabToDelete(null);
    }
  };

  const handleDeleteTab = (tabId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (tabs.length <= 1) {
      setError('Cannot delete the last tab');
      return;
    }
    // Guard on open: prevent opening delete dialog for favorite/default
    const tab = tabs.find((t) => t.id === tabId);
    const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
    const defaultId = Number(cfg?.defaultCollectionId ?? 0);
    if (tab && (tab.collectionId === favoriteId || (defaultId > 0 && tab.collectionId === defaultId))) {
      // Block silently; do not surface an error message
      return;
    }
    setSelectedTabToDelete(tabId);
    setIsDeleteDialogOpen(true);
  };

  const handleEditChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(event.target.value);
  };

  const validateTabName = (name: string): boolean => {
    if (!name.trim()) {
      setError('Tab name cannot be empty');
      return false;
    }
    if (tabs.some((tab) => tab.label === name && tab.id !== editingTab)) {
      setError('Tab name must be unique');
      return false;
    }
    return true;
  };

  const stopEditing = () => {
    if (editingTab) {
      if (validateTabName(editValue)) {
        setTabs(tabs.map((tab) => (tab.id === editingTab ? { ...tab, label: editValue.trim() } : tab)));

        // Persist the updated tab name *after* successful validation
        const tabToUpdate = tabs.find((tab) => tab.id === editingTab);
        if (tabToUpdate) {
          FigurService.updateCollectionDetails(
            tabToUpdate.collectionId,
            editValue.trim(),
            customerId,
            '', // Collection comment
            collections.find((c) => c.id === tabToUpdate.collectionId)?.headerComment ?? '',
          )
            .then(() => {
              // *Now* it's safe to reset the editing state
              setEditingTab(null);
              setError(null);
            })
            .catch((err) => {
              setError(`Failed to update tab name: ${err}`);
            });
        }
      }
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      stopEditing();
    }
  };

  const searchFieldSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiOutlinedInput-root': {
        backgroundColor: 'white',
      },
    }),
    [],
  );

  return (
    <FavoritesProvider>
      {isInitializing ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'grey.100',
            width: '100%',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}>
          <Box
            ref={containerRef}
            component="main"
            sx={{
              flexGrow: 1,
              width: '1400px',
              margin: '0 auto',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              pl: 3,
              pr: 0,
              pt: 3,
              bgcolor: 'grey.100',
              boxSizing: 'border-box',
            }}>
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                margin: '0 auto',
                boxSizing: 'border-box',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                border: 'none',
                bgcolor: 'transparent',
              }}>
              <Box
                sx={{
                  pb: 3,
                  pl: 3,
                  pr: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'hidden',
                  position: 'relative',
                }}>
                <TabContext value={value}>
                  <Box
                    sx={{
                      bgcolor: 'grey.100',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      position: 'relative',
                      zIndex: 1,
                      mb: 0,
                    }}>
                    <TabList
                      onChange={handleChange}
                      variant="scrollable"
                      scrollButtons="auto"
                      TabIndicatorProps={{
                        style: { display: 'none' },
                      }}
                      sx={{
                        minHeight: '32px !important',
                        '& .MuiTabs-indicator': {
                          display: 'none',
                        },
                        '& .MuiTabs-flexContainer': {
                          gap: 0,
                          bgcolor: 'grey.100',
                          minHeight: '32px !important',
                          height: '32px !important',
                        },
                        '& .MuiTabs-scroller': {
                          bgcolor: 'grey.100',
                          minHeight: '32px !important',
                          height: '32px !important',
                        },
                        '& .MuiTabs-scrollButtons': {
                          bgcolor: 'grey.100',
                          height: '32px !important',
                          minHeight: '32px !important',
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                          borderBottom: 'none',
                          borderRadius: '4px 4px 0 0',
                          '&.Mui-disabled': {
                            opacity: 0.3,
                          },
                        },
                        bgcolor: 'grey.100',
                        '& .MuiTab-root': {
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                          borderBottom: 'none',
                          borderRadius: '4px 4px 0 0',
                          marginLeft: '-1px',
                          padding: '0px 12px',
                          height: '32px !important',
                          minHeight: '32px !important',
                          lineHeight: '32px',
                          fontWeight: 600,
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          minWidth: 'unset',
                          bgcolor: 'grey.200',
                          color: 'text.primary',
                          '&.Mui-selected': {
                            backgroundColor: 'white',
                            fontWeight: 700,
                            color: 'text.primary',
                            marginBottom: '-1px',
                            borderBottom: '1px solid white',
                          },
                        },
                        '& .MuiTab-root:last-child': {
                          minWidth: 'unset',
                          width: 'auto',
                          padding: '0px 12px',
                          height: '32px !important',
                          minHeight: '32px !important',
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                          borderBottom: 'none',
                          borderRadius: '4px 4px 0 0',
                          backgroundColor: 'grey.200',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderBottom: 'none',
                            borderRadius: '4px 4px 0 0',
                            pointerEvents: 'none',
                          },
                        },
                        mb: 0,
                      }}>
                      {tabs.map((tab) => {
                        // Get collection color for this tab
                        const collection = collections.find((c) => c.id === tab.collectionId);
                        const favoriteId = Number(cfg?.favoritterCollectionId ?? 0);
                        const defaultId = Number(cfg?.defaultCollectionId ?? 0);
                        const isFavorite = tab.collectionId === favoriteId;
                        const isDefault = defaultId > 0 && tab.collectionId === defaultId;
                        const collectionColor = collection
                          ? getCollectionColors({
                              id: String(collection.id),
                              name: collection.name,
                              isDefault: isDefault,
                              color: collection.color,
                            }).bg
                          : undefined;

                        return (
                          <Tab
                            key={tab.id}
                            value={tab.id}
                            component={TabDiv}
                            onClick={() => handleTabClick(tab.id)}
                            sx={{
                              bgcolor: 'grey.200',
                              borderBottom: collectionColor ? `3px solid ${collectionColor} !important` : undefined,
                              '&.Mui-selected': {
                                bgcolor: 'white',
                                borderBottom: collectionColor ? `3px solid ${collectionColor} !important` : undefined,
                              },
                            }}
                            label={
                              <CustomTabContent
                                label={tab.label}
                                id={tab.id}
                                isEditing={editingTab === tab.id}
                                editValue={editValue}
                                error={error}
                                onEdit={() => startEditing(tab)}
                                onDelete={handleDeleteTab}
                                onEditChange={handleEditChange}
                                onKeyPress={handleKeyPress}
                                onStopEditing={stopEditing}
                                canDelete={!(isFavorite || isDefault)}
                                canEdit={!(isFavorite || isDefault)}
                              />
                            }
                            onDoubleClick={() => startEditing(tab)}
                          />
                        );
                      })}
                      <Box ref={addButtonRef} component={TabDiv} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                        <Button
                          onClick={handleAddButtonClick}
                          sx={{
                            minWidth: '32px',
                            padding: '2px 12px',
                            border: '1px solid rgba(0, 0, 0, 0.12)',
                            borderBottom: 'none',
                            borderRadius: '4px 4px 0 0',
                            marginLeft: '-1px',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            bgcolor: 'grey.200',
                            color: 'text.primary',
                            height: '32px',
                            minHeight: '32px',
                            lineHeight: '32px',
                            textTransform: 'none',
                            '&:hover': {
                              bgcolor: 'grey.300',
                            },
                          }}>
                          +
                        </Button>
                      </Box>
                    </TabList>
                    <Menu
                      open={Boolean(collectionMenuAnchor)}
                      anchorEl={collectionMenuAnchor}
                      onClose={handleCollectionMenuClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                      }}
                      MenuListProps={{
                        sx: {
                          py: 0,
                          maxHeight: 300,
                          width: '20ch',
                          overflow: 'hidden',
                        },
                      }}
                      PaperProps={{
                        sx: {
                          maxHeight: 300,
                          width: '20ch',
                          mt: 0.5,
                          overflow: 'hidden',
                        },
                      }}>
                      <Box ref={popoverContentRef}>
                        <Box sx={{ p: 1.5 }}>
                          <TextField
                            value={newCollectionName}
                            onChange={(e) => setNewCollectionName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                void handleCreateCollection();
                              }
                            }}
                            variant="standard"
                            fullWidth
                            autoFocus
                            sx={{
                              '& .MuiInputBase-root': {
                                fontSize: '0.875rem',
                              },
                              '& .MuiInputBase-input': {
                                fontSize: '0.875rem',
                              },
                            }}
                            inputProps={{ 'aria-label': 'Lag ny samling', placeholder: 'Lag ny samling' }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void handleCreateCollection();
                                    }}
                                    aria-label="Lag ny samling"
                                    disabled={!newCollectionName.trim()}>
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      </Box>
                    </Menu>
                  </Box>

                  {error && <Box sx={{ color: 'error.main', mb: 2, fontSize: '0.875rem' }}>{error}</Box>}

                  {/* Shared SearchField for current active tab */}
                  <Box
                    sx={{ pt: 2, pb: 0.625, px: 0, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-start', bgcolor: 'white' }}>
                    <Box sx={{ flex: 1 }}>
                      <SearchField
                        ref={searchFieldRef}
                        collectionId={tabs.find((tab) => tab.id === value)?.collectionId || Number(cfg?.favoritterCollectionId ?? 0)}
                        onSearchResults={handleSearchResults}
                        onSearchModeChange={handleSearchModeChange}
                        onForceGridView={handleForceGridView}
                        sx={searchFieldSx}
                        unifiedSearch={isUseAiEnabled}
                        placeholder={isUseAiEnabled ? 'Søk' : 'Søk i samling'}
                      />
                    </Box>
                  </Box>

                  {/* Series and Tags dropdowns under SearchField - hide when showing FigureDetail */}
                  {!tabs.find((tab) => tab.id === value)?.content && (
                    <Box
                      sx={{
                        pt: 0.5,
                        pb: 0,
                        px: 0,
                        mt: 0,
                        bgcolor: 'white',
                        borderTop: '1px solid white',
                        display: 'flex',
                        gap: 2,
                        alignItems: 'center',
                      }}>
                      <SeriesFilter value={seriesId} onChange={setSeriesId} />
                      <TagsFilter value={selectedTags} onChange={setSelectedTags} customerId={customerId} />
                    </Box>
                  )}

                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflow: 'auto',
                      width: '100%',
                      maxWidth: '100%',
                      margin: '0 auto',
                      bgcolor: 'white',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      mt: 0,
                      position: 'relative',
                      boxSizing: 'border-box',
                    }}>
                    {tabs.map((tab) => (
                      <TabPanel
                        key={tab.id}
                        value={tab.id}
                        sx={{
                          p: 3,
                          pt: 0.5,
                          width: '100%',
                          minHeight: '400px',
                          boxSizing: 'border-box',
                        }}>
                        {tab.content ? (
                          // If tab has custom content (like FigureDetail), render it
                          tab.content
                        ) : view === 'list' ? (
                          <ListView
                            collectionId={tab.collectionId}
                            view={view}
                            onViewChange={handleViewChange}
                            onFigureSelect={createFigureSelectHandler(tab.collectionId)}
                            onForceGridView={handleForceGridView}
                            sharedSearchState={searchState.collectionId === tab.collectionId ? searchState : null}
                            onAvatarClick={handleAvatarCollectionClick}
                            seriesId={seriesId}
                            selectedTags={selectedTags}
                            collections={collections}
                          />
                        ) : (
                          <CardView
                            collectionId={tab.collectionId}
                            view={view}
                            onViewChange={handleViewChange}
                            onFigureSelect={createFigureSelectHandler(tab.collectionId)}
                            onForceGridView={handleForceGridView}
                            sharedSearchState={searchState.collectionId === tab.collectionId ? searchState : null}
                            onAvatarClick={handleAvatarCollectionClick}
                            seriesId={seriesId}
                            selectedTags={selectedTags}
                            collections={collections}
                          />
                        )}
                      </TabPanel>
                    ))}
                  </Box>
                </TabContext>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}

      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
        <DialogTitle>Bekreft sletting</DialogTitle>
        <DialogContent>
          <DialogContentText>Er du sikker på at du vil slette denne fanen og alle dens figurer?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Avbryt</Button>
          <Button onClick={handleDeleteConfirmed} color="error" variant="contained">
            Slett
          </Button>
        </DialogActions>
      </Dialog>
    </FavoritesProvider>
  );
};

export default CustomTab;
